import React, { useCallback, useEffect, useRef, useState } from 'react';
import Book from './Book';
import { bookMetrics, fitSpineWidths, SHELF_GAP, SHELF_PAD } from './bookMetrics';

const countLabel = (n) => `${n} ${n === 1 ? 'book' : 'books'}`;
const bookId = (book) => book.goodreadsUrl || book.title;

/**
 * Which edges of a scrolling shelf have books hidden past them. A year with
 * nineteen books runs well off the page, so the shelf fades at whichever end
 * still has more to see -- and fades neither end when everything fits.
 */
export function scrollEdges(scrollLeft, scrollWidth, clientWidth) {
  const slack = 2; // sub-pixel scroll positions shouldn't count as "more"
  return {
    start: scrollLeft > slack,
    end: scrollLeft + clientWidth < scrollWidth - slack,
  };
}

/**
 * One shelf of the bookcase: a row of books standing on an ink rule, with the
 * shelf's label and count beneath it. Long shelves scroll sideways.
 *
 * mode="face" makes every book face out (the "reading now" shelf).
 * mode="spine" stands every book upright on its spine (the year shelves).
 */
export default function Shelf({
  label,
  books,
  mode = 'spine',
  variant = 'current',
  openId,
  onToggle,
  emptyText,
}) {
  const scrollerRef = useRef(null);
  const [shelf, setShelf] = useState({ width: 0, start: false, end: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setShelf((current) => {
      const edges = scrollEdges(el.scrollLeft, el.scrollWidth, el.clientWidth);
      const next = { width: el.clientWidth, ...edges };
      return next.width === current.width &&
        next.start === current.start &&
        next.end === current.end
        ? current
        : next;
    });
  }, []);

  // Re-measure on scroll, on resize, and whenever a book opens or closes --
  // an open book widens its slot, which can push the shelf past the edge.
  useEffect(() => {
    measure();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => {
        el.removeEventListener('scroll', measure);
        window.removeEventListener('resize', measure);
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure, books, openId]);

  // Spines stretch to fill the shelf; face-out covers are sized by the artwork.
  const spineWidths =
    mode === 'spine'
      ? fitSpineWidths(books.map((book) => bookMetrics(book).spineWidth), shelf.width)
      : [];

  return (
    <section className={`shelf shelf--${variant}`} aria-label={label}>
      <div
        className="shelf-back"
        data-more-left={shelf.start || undefined}
        data-more-right={shelf.end || undefined}
      >
        <div className="shelf-scroller" ref={scrollerRef}>
          {books.length > 0 ? (
            <div
              className="shelf-row"
              style={{ '--shelf-gap': `${SHELF_GAP}px`, '--shelf-pad': `${SHELF_PAD}px` }}
            >
              {books.map((book, i) => (
                <Book
                  key={bookId(book)}
                  book={book}
                  mode={mode}
                  index={i}
                  spineWidth={spineWidths[i]}
                  isOpen={openId === bookId(book)}
                  onToggle={onToggle}
                  scrollerRef={scrollerRef}
                />
              ))}
            </div>
          ) : (
            <p className="shelf-empty">{emptyText}</p>
          )}
        </div>
      </div>
      <div className="shelf-board">
        <span className="shelf-label">{label}</span>
        <span className="shelf-count">{countLabel(books.length)}</span>
      </div>
    </section>
  );
}
